import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DEFAULT_BLOCK_TYPES } from '../../hooks/useTimeblock';
import { updateSetting } from '../../store/settingsSlice';
import { parseISO, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

function formatTime(isoOrHHMM) {
    if (!isoOrHHMM) return '';
    if (/^\d{2}:\d{2}$/.test(isoOrHHMM)) return isoOrHHMM;
    try {
        return format(parseISO(isoOrHHMM), 'h:mm a');
    } catch {
        return isoOrHHMM;
    }
}

function formatElapsed(secs) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
}

const timeInputClass = 'w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)] [color-scheme:dark]';

// ---- Block dialog ----
function BlockDialog({ open, initial, blockTypes, onSave, onClose }) {
    const [blockTypeId, setBlockTypeId] = useState(initial?.blockTypeId || (blockTypes[0]?.id ?? ''));
    const [startTime, setStartTime] = useState(initial?.startTime || '09:00');
    const [endTime, setEndTime] = useState(initial?.endTime || '10:00');

    if (!open) return null;

    const handleSave = () => {
        if (!blockTypeId || !startTime || !endTime) return;
        onSave({ blockTypeId, startTime, endTime });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[9500] flex items-center justify-center p-4">
            <div className="bg-[var(--surface-bg)] rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
                <h3 className="text-base font-semibold text-[var(--text-primary)]">
                    {initial ? 'Edit Block' : 'Add Block'}
                </h3>

                <div className="space-y-3">
                    <div>
                        <label className="block text-xs text-[var(--text-muted)] mb-1">Block Type</label>
                        <select
                            value={blockTypeId}
                            onChange={e => setBlockTypeId(e.target.value)}
                            className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)]"
                        >
                            {blockTypes.map(bt => (
                                <option key={bt.id} value={bt.id}>{bt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block text-xs text-[var(--text-muted)] mb-1">Start Time</label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={e => setStartTime(e.target.value)}
                                className={timeInputClass}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs text-[var(--text-muted)] mb-1">End Time</label>
                            <input
                                type="time"
                                value={endTime}
                                onChange={e => setEndTime(e.target.value)}
                                className={timeInputClass}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-center gap-3 pt-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!blockTypeId || !startTime || !endTime}
                        className="px-4 py-2 bg-[var(--primary-button)] text-white text-sm rounded hover:bg-[var(--primary-button-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        {initial ? 'Save' : 'Add'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Quick-toggle bar (all block types; smaller on mobile) ----
function QuickToggleBar({ blockTypes, activeTimer, elapsed, dayData, onToggle }) {
    return (
        <div className="grid grid-cols-4 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
            {blockTypes.map(bt => {
                const isActive =
                    activeTimer?.blockId === bt.id ||
                    (activeTimer && dayData.scheduledBlocks.find(
                        b => b.id === activeTimer.blockId && b.blockTypeId === bt.id
                    ));

                return (
                    <button
                        key={bt.id}
                        onClick={() => onToggle(bt.id)}
                        className={`flex flex-col items-center justify-center gap-0.5 sm:gap-1.5 aspect-square sm:aspect-auto sm:w-24 sm:h-24 rounded-lg sm:rounded-xl border-2 font-semibold transition-all select-none min-h-0 ${
                            isActive ? 'shadow-lg' : 'hover:opacity-80'
                        }`}
                        style={isActive
                            ? { backgroundColor: bt.color, borderColor: bt.color, color: '#fff' }
                            : { backgroundColor: `${bt.color}15`, borderColor: bt.color, color: bt.color }
                        }
                    >
                        {isActive ? (
                            <>
                                <span className="material-symbols-outlined text-base sm:text-[22px] opacity-90">pause</span>
                                <span className="text-[10px] sm:text-sm font-semibold leading-tight text-center px-0.5 sm:px-1 truncate w-full">{bt.label}</span>
                                <span className="font-mono text-[10px] sm:text-base font-bold tracking-tight opacity-90">{formatElapsed(elapsed)}</span>
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-base sm:text-[22px] opacity-70">play_arrow</span>
                                <span className="text-[10px] sm:text-sm font-semibold leading-tight text-center px-0.5 sm:px-1 truncate w-full">{bt.label}</span>
                            </>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// ---- Save Template Modal ----
function SaveTemplateModal({ open, scheduledBlocks, onClose }) {
    const dispatch = useDispatch();
    const settings = useSelector(state => state.settings.settings);
    const [name, setName] = useState('');

    if (!open) return null;

    const handleSave = () => {
        if (!name.trim()) return;
        const template = {
            id: uuidv4(),
            name: name.trim(),
            blocks: scheduledBlocks.map(({ blockTypeId, startTime, endTime }) => ({ blockTypeId, startTime, endTime }))
        };
        const existing = settings?.timeblockTemplates || [];
        dispatch(updateSetting({ key: 'timeblockTemplates', value: [...existing, template] }));
        setName('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[9500] flex items-center justify-center p-4">
            <div className="bg-[var(--surface-bg)] rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
                <h3 className="text-base font-semibold text-[var(--text-primary)]">Save as Template</h3>
                <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Template Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        placeholder="e.g. Standard Workday"
                        autoFocus
                        className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--primary-button)]"
                    />
                    <p className="mt-1.5 text-xs text-[var(--text-muted)]">
                        {scheduledBlocks.length} block{scheduledBlocks.length !== 1 ? 's' : ''} will be saved
                    </p>
                </div>
                <div className="flex justify-center gap-3 pt-1">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!name.trim()}
                        className="px-4 py-2 bg-[var(--primary-button)] text-white text-sm rounded hover:bg-[var(--primary-button-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Load Template Modal ----
function LoadTemplateModal({ open, blockTypes, onLoad, onClose }) {
    const dispatch = useDispatch();
    const settings = useSelector(state => state.settings.settings);
    const templates = settings?.timeblockTemplates || [];

    const [selected, setSelected] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    if (!open) return null;

    const getBlockType = (id) => blockTypes.find(bt => bt.id === id) || { label: id, color: '#888' };

    const saveTemplates = (updated) => {
        dispatch(updateSetting({ key: 'timeblockTemplates', value: updated }));
    };

    const handleRenameStart = (t) => {
        setRenamingId(t.id);
        setRenameValue(t.name);
        setDeleteConfirmId(null);
    };

    const handleRenameSave = () => {
        if (!renameValue.trim()) return;
        saveTemplates(templates.map(t => t.id === renamingId ? { ...t, name: renameValue.trim() } : t));
        setRenamingId(null);
    };

    const handleDelete = (id) => {
        saveTemplates(templates.filter(t => t.id !== id));
        if (selected === id) setSelected(null);
        setDeleteConfirmId(null);
    };

    const handleLoad = () => {
        const template = templates.find(t => t.id === selected);
        if (!template) return;
        onLoad(template.blocks);
        setSelected(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[9500] flex items-center justify-center p-4">
            <div className="bg-[var(--surface-bg)] rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
                <h3 className="text-base font-semibold text-[var(--text-primary)]">Load Template</h3>

                {templates.length === 0 ? (
                    <p className="text-sm text-[var(--text-muted)] py-4 text-center">
                        No templates saved yet. Add blocks to a day and save it as a template.
                    </p>
                ) : (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                        {templates.map(t => (
                            <div
                                key={t.id}
                                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-colors cursor-pointer ${
                                    selected === t.id
                                        ? 'border-[var(--primary-button)] bg-[var(--primary-button)]/10'
                                        : 'border-[var(--input-border)] hover:border-[var(--input-border-hover)] hover:bg-[var(--button-hover-bg)]'
                                }`}
                            >
                                {renamingId === t.id ? (
                                    <>
                                        <input
                                            type="text"
                                            value={renameValue}
                                            onChange={e => setRenameValue(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleRenameSave(); if (e.key === 'Escape') setRenamingId(null); }}
                                            autoFocus
                                            className="flex-1 bg-[var(--input-bg)] border border-[var(--input-border)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary-button)]"
                                        />
                                        <button onClick={handleRenameSave} className="px-1 py-1 text-[var(--primary-button)] hover:opacity-80">
                                            <span className="material-symbols-outlined text-[18px]">check</span>
                                        </button>
                                        <button onClick={() => setRenamingId(null)} className="px-1 py-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
                                            <span className="material-symbols-outlined text-[18px]">close</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            className="flex-1 text-left"
                                            onClick={() => setSelected(selected === t.id ? null : t.id)}
                                        >
                                            <span className="text-sm font-medium text-[var(--text-primary)] block">{t.name}</span>
                                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                                {t.blocks.map((b, i) => {
                                                    const bt = getBlockType(b.blockTypeId);
                                                    return (
                                                        <span
                                                            key={i}
                                                            className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]"
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: bt.color }} />
                                                            {bt.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => { handleRenameStart(t); setDeleteConfirmId(null); }}
                                            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                        </button>
                                        {deleteConfirmId === t.id ? (
                                            <div className="flex -space-x-2 flex-shrink-0">
                                                <button
                                                    onClick={() => handleDelete(t.id)}
                                                    className="px-1.5 py-1 text-[var(--error)] hover:text-[var(--error-hover)]"
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">check</span>
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirmId(null)}
                                                    className="px-1.5 py-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">close</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => { setDeleteConfirmId(t.id); setRenamingId(null); }}
                                                className="px-1.5 py-1 text-[var(--error)] hover:text-[var(--error-hover)] flex-shrink-0"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex justify-center gap-3 pt-1">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleLoad}
                        disabled={!selected}
                        className="px-4 py-2 bg-[var(--primary-button)] text-white text-sm rounded hover:bg-[var(--primary-button-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        Load
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Main Schedule component ----
function Schedule({ hook }) {
    const {
        selectedDate, setSelectedDate,
        dayData, activeTimer, elapsed, loading, saving,
        addBlock, editBlock, deleteBlock, loadTemplate, startTimer, stopTimer
    } = hook;

    const settings = useSelector(state => state.settings.settings);
    const blockTypes = settings?.timeblockTypes || DEFAULT_BLOCK_TYPES;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
    const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);

    const getBlockType = (id) => blockTypes.find(bt => bt.id === id) || { label: id, color: '#888' };

    const handleAddClick = () => {
        setEditTarget(null);
        setDialogOpen(true);
    };

    const handleEditClick = (block) => {
        setEditTarget(block);
        setDialogOpen(true);
    };

    const handleDialogSave = ({ blockTypeId, startTime, endTime }) => {
        if (editTarget) {
            editBlock(editTarget.id, { blockTypeId, startTime, endTime });
        } else {
            addBlock(blockTypeId, startTime, endTime);
        }
    };

    const handleTimerToggle = (blockId) => {
        if (activeTimer?.blockId === blockId) {
            stopTimer();
        } else {
            if (activeTimer) stopTimer();
            startTimer(blockId);
        }
    };

    // Quick-toggle: keyed by blockTypeId directly
    const handleQuickToggle = (blockTypeId) => {
        const isActiveViaSchedule = activeTimer && dayData.scheduledBlocks.find(
            b => b.id === activeTimer.blockId && b.blockTypeId === blockTypeId
        );
        const isActiveViaQuick = activeTimer?.blockId === blockTypeId;

        if (isActiveViaSchedule || isActiveViaQuick) {
            stopTimer();
        } else {
            if (activeTimer) stopTimer();
            startTimer(blockTypeId);
        }
    };

    return (
        <div className="space-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        value={selectedDate}
                        onChange={(d) => d && setSelectedDate(d)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: {
                                    '& .MuiInputBase-root': {
                                        backgroundColor: 'var(--input-bg)',
                                        color: 'var(--text-primary)',
                                        fontSize: '0.875rem'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--input-border)'
                                    },
                                    '& .MuiSvgIcon-root': { color: 'var(--text-muted)' }
                                }
                            }
                        }}
                    />
                </LocalizationProvider>

                <div className="flex items-center gap-2">
                    {saving && (
                        <span className="text-xs text-[var(--text-muted)]">Saving…</span>
                    )}
                    <button
                        onClick={() => setLoadTemplateOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)]"
                        title="Load template"
                    >
                        <span className="material-symbols-outlined text-base">folder_open</span>
                        Templates
                    </button>
                    <button
                        onClick={() => setSaveTemplateOpen(true)}
                        disabled={dayData.scheduledBlocks.length === 0}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--button-hover-bg)] rounded transition-colors border border-[var(--input-border)] disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Save current schedule as template"
                    >
                        <span className="material-symbols-outlined text-base">bookmark_add</span>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary-button)] text-white text-sm rounded hover:bg-[var(--primary-button-hover)] transition-colors"
                    >
                        <span className="material-symbols-outlined text-base">add</span>
                        Add Block
                    </button>
                </div>
            </div>

            {/* Quick-toggle bar */}
            <QuickToggleBar
                blockTypes={blockTypes}
                activeTimer={activeTimer}
                elapsed={elapsed}
                dayData={dayData}
                onToggle={handleQuickToggle}
            />

            {/* Block list */}
            {loading ? (
                <div className="text-center py-12 text-[var(--text-muted)] text-sm">Loading…</div>
            ) : dayData.scheduledBlocks.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-muted)] text-sm">
                    No blocks scheduled for this day.
                    <br />
                    <button
                        onClick={handleAddClick}
                        className="mt-2 text-[var(--primary-button)] hover:underline"
                    >
                        Add the first block
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {dayData.scheduledBlocks.map(block => {
                        const bt = getBlockType(block.blockTypeId);
                        const isActive = activeTimer?.blockId === block.id;

                        return (
                            <div
                                key={block.id}
                                className={`flex items-center gap-3 rounded-lg border transition-all ${
                                    isActive ? 'shadow-md' : ''
                                }`}
                                style={{
                                    borderColor: isActive ? bt.color : 'var(--surface-border)',
                                    backgroundColor: isActive ? `${bt.color}18` : 'var(--surface-bg-lighter)'
                                }}
                            >
                                {/* Color bar */}
                                <div
                                    className="w-1.5 self-stretch rounded-l-lg flex-shrink-0"
                                    style={{ backgroundColor: bt.color }}
                                />

                                {/* Play/stop button */}
                                <button
                                    onClick={() => handleTimerToggle(block.id)}
                                    className="p-1.5 rounded-full hover:bg-[var(--button-hover-bg)] transition-colors flex-shrink-0"
                                    title={isActive ? 'Stop timer' : 'Start timer'}
                                >
                                    <span className="material-symbols-outlined text-xl" style={{ color: bt.color }}>
                                        {isActive ? 'stop_circle' : 'play_circle'}
                                    </span>
                                </button>

                                {/* Info */}
                                <div className="flex-1 py-3 min-w-0">
                                    <span className="text-sm font-medium text-[var(--text-primary)] truncate block">
                                        {bt.label}
                                    </span>
                                    {isActive ? (
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <span
                                                className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0"
                                                style={{ backgroundColor: bt.color }}
                                            />
                                            <span className="text-xs font-mono" style={{ color: bt.color }}>
                                                {formatElapsed(elapsed)}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-[var(--text-muted)] mt-0.5">
                                            {formatTime(block.startTime)} – {formatTime(block.endTime)}
                                        </div>
                                    )}
                                </div>

                                {pendingDeleteId === block.id ? (
                                    <div className="flex -space-x-2 pr-1">
                                        <button
                                            onClick={() => { deleteBlock(block.id); setPendingDeleteId(null); }}
                                            className="px-2 py-2 text-[var(--error)] hover:text-[var(--error-hover)]"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">check</span>
                                        </button>
                                        <button
                                            onClick={() => setPendingDeleteId(null)}
                                            className="px-2 py-2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">close</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <button
                                            onClick={() => handleEditClick(block)}
                                            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-base">edit</span>
                                        </button>
                                        <button
                                            onClick={() => setPendingDeleteId(block.id)}
                                            className="px-2 py-2 text-[var(--error)] hover:text-[var(--error-hover)]"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <BlockDialog
                key={editTarget?.id ?? 'new'}
                open={dialogOpen}
                initial={editTarget}
                blockTypes={blockTypes}
                onSave={handleDialogSave}
                onClose={() => { setDialogOpen(false); setEditTarget(null); }}
            />

            <SaveTemplateModal
                open={saveTemplateOpen}
                scheduledBlocks={dayData.scheduledBlocks}
                onClose={() => setSaveTemplateOpen(false)}
            />

            <LoadTemplateModal
                open={loadTemplateOpen}
                blockTypes={blockTypes}
                onLoad={loadTemplate}
                onClose={() => setLoadTemplateOpen(false)}
            />
        </div>
    );
}

export default Schedule;
